/**
 * Documents: FusionDocs (the group DMS) working inside ONE. Folder tree, document list and preview side by side on a
 * desktop; stacked on a phone with the preview full screen. Folder browsing lights up by itself once /api/fd/home
 * says browse: true; until then the start view is "Filed from ONE", renewals and search.
 */
import { ShareButton } from "../components/documents/Share";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import clsx from "clsx";
import { CalendarClock, FolderOpen, FolderPlus, Inbox, Loader2, MessageSquareText, Search, Upload, X } from "lucide-react";
import { useState, type ReactNode } from "react";
import { api } from "../lib/api";
import { Card, Empty, ErrorNote, PageHeader } from "../components/ui";
import { AskPanel } from "../components/documents/Ask";
import { DocRow } from "../components/documents/DocMeta";
import { FolderTree } from "../components/documents/FolderTree";
import { DocPreview } from "../components/documents/Preview";
import { UploadDialog } from "../components/documents/Upload";
import { day, errText, FK, idFromUrl, isOff, useDebounced, useWide, type FdDoc, type FdHit, type FdHome, type FdList, type FdObligation } from "../components/documents/fd";

export function DocumentsPage() {
  const qc = useQueryClient();
  const params = useParams({ strict: false }) as { id?: string };
  const openId = params.id ?? null;
  const navigate = useNavigate();
  const wide = useWide();
  const open = (id: string) => void navigate({ to: "/documents/$id", params: { id } });
  const close = () => void navigate({ to: "/documents" });

  const [q, setQ] = useState("");
  const dq = useDebounced(q.trim(), 300);
  const [folder, setFolder] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [asking, setAsking] = useState(false);

  const home = useQuery({ queryKey: FK.home, queryFn: () => api<FdHome>("/api/fd/home"), retry: false, staleTime: 60_000 });
  const browse = home.data?.browse === true;

  if (home.error && isOff(home.error)) {
    return (
      <div>
        <PageHeader title="Documents" />
        <div className="panel"><Empty icon={<FolderOpen size={18} />}>FusionDocs isn't switched on in ONE yet. Your documents will appear here as soon as it is.</Empty></div>
      </div>
    );
  }

  const searching = dq.length >= 2;
  const startView = !searching && folder === null;
  const showPreview = openId && wide;

  return (
    <div className="flex min-h-full flex-col">
      <PageHeader
        title="Documents"
        subtitle="Every document in FusionDocs that you are allowed to see."
        actions={<>
          <button className="btn" onClick={() => setAsking(true)}><MessageSquareText size={15} /> Ask</button>
          <button className="btn btn-primary" onClick={() => setUploading(true)}><Upload size={15} /> Upload</button>
        </>}
      />

      <div className="relative mb-5">
        <Search size={18} className="muted pointer-events-none absolute left-4 top-1/2 -translate-y-1/2" aria-hidden />
        <input
          type="search"
          className="input !min-h-[3rem] w-full !rounded-xl !pl-11 !pr-11 text-[0.9375rem]"
          placeholder="Search all documents"
          aria-label="Search all documents"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Escape" && setQ("")}
        />
        {q && <button className="btn btn-ghost btn-icon absolute right-1.5 top-1/2 !h-9 !min-h-0 !w-9 -translate-y-1/2" onClick={() => setQ("")} aria-label="Clear search"><X size={16} /></button>}
      </div>

      {home.error && <div className="mb-4"><ErrorNote error={home.error} /></div>}

      <div
        className={clsx(
          "grid min-h-0 flex-1 gap-5",
          browse && showPreview && "lg:grid-cols-[230px_minmax(0,1fr)_minmax(0,1.25fr)]",
          browse && !showPreview && "lg:grid-cols-[230px_minmax(0,1fr)]",
          !browse && showPreview && "lg:grid-cols-[minmax(0,1fr)_minmax(0,1.25fr)]",
        )}
      >
        {browse && (
          <aside className="panel h-fit p-2 lg:sticky lg:top-0 lg:max-h-[calc(100vh-12rem)] lg:overflow-auto scroll-thin">
            <FolderTree folders={home.data?.folders ?? []} unfiled={home.data?.unfiled} total={home.data?.total} selected={folder} onSelect={(p) => { setFolder(p); setQ(""); }} />
            <NewFolder parent={folder} onCreated={(p) => { void qc.invalidateQueries({ predicate: (x) => x.queryKey[0] === "fd" }); setFolder(p); }} />
          </aside>
        )}

        <div className="min-w-0 space-y-5">
          {home.isLoading ? (
            <Empty><Loader2 size={14} className="mr-1 inline animate-spin" /> Loading documents…</Empty>
          ) : browse ? (
            <>
              {startView && <Deadlines onOpen={open} />}
              <BrowseList folder={folder} q={searching ? dq : ""} openId={openId} onOpen={open} />
              {startView && <FiledFromOne items={home.data?.filedFromOne ?? []} openId={openId} onOpen={open} />}
            </>
          ) : searching ? (
            <SearchResults q={dq} openId={openId} onOpen={open} />
          ) : (
            <>
              <Deadlines onOpen={open} />
              <FiledFromOne items={home.data?.filedFromOne ?? []} openId={openId} onOpen={open} />
              {home.data && <p className="muted text-xs">Folder browsing arrives shortly. Search finds every document you can see.</p>}
            </>
          )}
        </div>

        {showPreview && (
          <div className="min-w-0 lg:sticky lg:top-0 lg:h-[calc(100vh-12rem)]">
            <DocPreview key={openId} id={openId} onClose={close} fullScreen={false} />
          </div>
        )}
      </div>

      {openId && !wide && <DocPreview key={openId} id={openId} onClose={close} fullScreen />}
      {uploading && <UploadDialog onClose={() => setUploading(false)} onOpen={open} />}
      {asking && <AskPanel onClose={() => setAsking(false)} onOpen={open} />}
    </div>
  );
}

function ListCard({ title, icon, count, children, actions }: { title: string; icon: ReactNode; count?: number; children: ReactNode; actions?: ReactNode }) {
  return <Card title={title} icon={icon} count={count} actions={actions} flush>{children}</Card>;
}

/** Browse mode: one folder, a search, or everything, with filters and "load more". */
function BrowseList({ folder, q, openId, onOpen }: { folder: string | null; q: string; openId: string | null; onOpen: (id: string) => void }) {
  const [type, setType] = useState("");
  const [company, setCompany] = useState("");
  const [sort, setSort] = useState<"recent" | "title">("recent");
  const [mine, setMine] = useState(false);
  const fType = useDebounced(type.trim(), 400);
  const fCompany = useDebounced(company.trim(), 400);
  const base: Record<string, string> = { sort, limit: "50" };
  // folder "" is sent as-is: it means not yet filed
  if (folder !== null) base.folder = folder;
  if (q) base.q = q;
  if (fType) base.type = fType;
  if (fCompany) base.company = fCompany;
  if (mine) base.mine = "1";

  const list = useInfiniteQuery({
    queryKey: FK.list(base),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => {
      const p = new URLSearchParams(base);
      if (pageParam) p.set("cursor", pageParam);
      return api<FdList>(`/api/fd/documents?${p.toString()}`);
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    retry: false,
  });
  const items = list.data?.pages.flatMap((p) => p.items) ?? [];
  const total = list.data?.pages[0]?.total ?? (list.hasNextPage ? undefined : items.length);
  const title = q ? `Results for "${q}"` : folder === "" ? "Not yet filed" : folder ? folder.split("/").filter(Boolean).pop() ?? folder : "All documents";

  return (
    <ListCard title={title} icon={folder === "" && !q ? <Inbox size={16} /> : <FolderOpen size={16} />} count={list.data ? total ?? undefined : undefined}
      actions={folder ? <ShareButton target={{ kind: "folder", path: folder }} /> : undefined}>
      {folder === "" && !q && <p className="muted px-4 pb-2 text-xs sm:px-5">Documents FusionDocs hasn't put in a folder yet. Open one and use Move to file it.</p>}
      <div className="flex flex-wrap items-center gap-2 border-b border-[var(--line)] px-4 pb-3 sm:px-5">
        <input className="input !min-h-0 w-[8.5rem] !py-1.5 text-xs" placeholder="Type" aria-label="Filter by type" value={type} onChange={(e) => setType(e.target.value)} />
        <input className="input !min-h-0 w-[8.5rem] !py-1.5 text-xs" placeholder="Company" aria-label="Filter by company" value={company} onChange={(e) => setCompany(e.target.value)} />
        <select className="input !min-h-0 !py-1.5 text-xs" aria-label="Sort" value={sort} onChange={(e) => setSort(e.target.value as "recent" | "title")}>
          <option value="recent">Most recent</option>
          <option value="title">Title A to Z</option>
        </select>
        <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} /> Only mine</label>
      </div>
      {list.error ? <div className="p-4"><ErrorNote error={list.error} /></div>
        : list.isLoading ? <Empty><Loader2 size={14} className="mr-1 inline animate-spin" /> Loading…</Empty>
        : items.length === 0 ? <Empty icon={<Inbox size={18} />}>{q ? "Nothing matches that search." : folder === "" ? "Everything you can see is filed." : "No documents here yet."}</Empty>
        : (
          <ul className="divide-y divide-[var(--line)]">
            {items.map((d) => <DocRow key={d.id} doc={d} active={d.id === openId} onOpen={onOpen} thumb />)}
          </ul>
        )}
      {list.hasNextPage && (
        <div className="px-4 pt-2 sm:px-5">
          <button className="btn btn-sm w-full" disabled={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
            {list.isFetchingNextPage ? <Loader2 size={14} className="animate-spin" /> : null} Load more
            {total != null && <span className="muted num">({items.length.toLocaleString("en-GB")} of {total.toLocaleString("en-GB")})</span>}
          </button>
        </div>
      )}
    </ListCard>
  );
}

/** Search before folder browsing exists: FusionDocs' own search. */
function SearchResults({ q, openId, onOpen }: { q: string; openId: string | null; onOpen: (id: string) => void }) {
  const s = useQuery({
    queryKey: FK.search(q),
    queryFn: () => api<{ ok: boolean; hits: FdHit[] }>(`/api/fusiondocs/search?q=${encodeURIComponent(q)}`),
    retry: false,
    staleTime: 30_000,
  });
  const docs: FdDoc[] = (s.data?.hits ?? []).map((h) => ({ id: h.id, title: h.title, type: h.type, updatedAt: h.updatedAt, url: h.url, filingPath: h.filingPath, snippet: h.snippet || h.summary }));
  return (
    <ListCard title={`Results for "${q}"`} icon={<Search size={16} />} count={docs.length}>
      {s.error ? <div className="p-4"><ErrorNote error={new Error(errText(s.error))} /></div>
        : s.isLoading ? <Empty><Loader2 size={14} className="mr-1 inline animate-spin" /> Searching…</Empty>
        : s.data && !s.data.ok ? <Empty>FusionDocs didn't answer. Try again in a moment.</Empty>
        : docs.length === 0 ? <Empty icon={<Search size={18} />}>Nothing matches that search.</Empty>
        : <ul className="divide-y divide-[var(--line)]">{docs.map((d) => <DocRow key={d.id} doc={d} active={d.id === openId} onOpen={onOpen} thumb />)}</ul>}
    </ListCard>
  );
}

const FILED_STATUS: Record<string, { label: string; tone: string }> = {
  processing: { label: "Scanning", tone: "bg-amber-50 text-amber-800 ring-amber-200" },
  rejected: { label: "Refused", tone: "bg-red-50 text-red-700 ring-red-200" },
  quarantined: { label: "Quarantined", tone: "bg-red-50 text-red-700 ring-red-200" },
  scan_failed: { label: "Scan failed", tone: "bg-red-50 text-red-700 ring-red-200" },
};

function FiledFromOne({ items, openId, onOpen }: { items: FdHome["filedFromOne"]; openId: string | null; onOpen: (id: string) => void }) {
  return (
    <ListCard title="Filed from ONE" icon={<Upload size={16} />} count={items.length}>
      {items.length === 0 ? (
        <Empty icon={<FolderOpen size={18} />}>Nothing filed from ONE yet. Use Upload, or file a task's attachment to FusionDocs.</Empty>
      ) : (
        <ul className="divide-y divide-[var(--line)]">
          {items.map((f) => {
            const st = FILED_STATUS[f.status];
            return (
              <DocRow key={f.id} doc={{ id: f.id, title: f.title, updatedAt: f.filedAt }} active={f.id === openId} onOpen={onOpen}
                extra={st && <span className={clsx("badge mt-1 ring-1", st.tone)}>{st.label}</span>} />
            );
          })}
        </ul>
      )}
    </ListCard>
  );
}

/** Renewals and deadlines in the next 90 days; each opens its document. */
function Deadlines({ onOpen }: { onOpen: (id: string) => void }) {
  const o = useQuery({ queryKey: FK.obligations, queryFn: () => api<{ items: FdObligation[] }>("/api/fd/obligations?days=90"), retry: false, staleTime: 10 * 60_000 });
  const items = [...(o.data?.items ?? [])].sort((a, b) => a.dueAt.localeCompare(b.dueAt));
  if (o.isLoading || (o.error && isOff(o.error))) return null;
  return (
    <ListCard title="Renewals and deadlines" icon={<CalendarClock size={16} />} count={items.length} actions={<span className="muted text-xs">Next 90 days</span>}>
      {o.error ? <p className="muted px-5 pb-3 text-sm">Couldn't load deadlines: {errText(o.error)}</p>
        : items.length === 0 ? <p className="muted px-5 pb-3 text-sm">Nothing due in the next 90 days.</p>
        : (
          <ul className="divide-y divide-[var(--line)]">
            {items.map((it) => {
              const id = it.documentId ?? idFromUrl(it.url);
              const days = Math.ceil((new Date(it.dueAt).getTime() - Date.now()) / 86_400_000);
              const tone = days <= 14 ? "text-red-600" : days <= 30 ? "text-amber-600" : "text-[var(--text-2)]";
              const body = (
                <>
                  <span className={clsx("w-14 shrink-0 text-center", tone)}>
                    <span className="block text-lg font-bold leading-none num">{Math.max(0, days)}</span>
                    <span className="text-[0.65rem] font-semibold uppercase tracking-wide">{days === 1 ? "day" : "days"}</span>
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold">{it.documentTitle || "Document"}</span>
                    <span className="muted block truncate text-xs">
                      {[it.kind ? it.kind.replace(/_/g, " ") : null, `due ${day(it.dueAt)}`, it.noticePeriod ? `notice ${it.noticePeriod}` : null].filter(Boolean).join(" · ")}
                    </span>
                  </span>
                </>
              );
              const cls = "flex w-full items-center gap-3 px-4 py-2.5 text-left sm:px-5";
              return (
                <li key={it.id}>
                  {id ? <button type="button" className={`${cls} hover:bg-[var(--panel-2)]`} onClick={() => onOpen(id)}>{body}</button> : <div className={cls}>{body}</div>}
                </li>
              );
            })}
          </ul>
        )}
    </ListCard>
  );
}

/** "New folder" inside the selected folder (or at the top, where the first level must be a company). */
function NewFolder({ parent, onCreated }: { parent: string | null; onCreated: (path: string) => void }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [err, setErr] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const base = parent ? `${parent}/` : "";
  const save = async () => {
    setBusy(true); setErr(null);
    try { const r = await api<{ path: string }>("/api/fd/folders", { method: "PUT", json: { path: `${base}${name.trim()}` } }); setOpen(false); setName(""); onCreated(r.path); }
    catch (e) { setErr(e); } finally { setBusy(false); }
  };
  if (!open) return <button type="button" className="btn btn-sm mt-2 w-full" onClick={() => setOpen(true)}><FolderPlus size={14} /> New folder</button>;
  return (
    <div className="mt-2 space-y-2 rounded-lg border border-[var(--line)] p-2">
      <p className="muted text-xs">{parent ? <>Inside <b>{parent}</b></> : "At the top, start with a company: IFG, IFT, IFS, IFI, IFP or Mythos"}</p>
      <input className="input" autoFocus placeholder="Folder name" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && name.trim()) void save(); if (e.key === "Escape") setOpen(false); }} aria-label="New folder name" />
      <ErrorNote error={err} />
      <div className="flex gap-2"><button type="button" className="btn btn-sm btn-primary" disabled={!name.trim() || busy} onClick={() => void save()}>Create</button><button type="button" className="btn btn-sm" onClick={() => setOpen(false)}>Cancel</button></div>
    </div>
  );
}
